import { ActivePeriod } from 'models/prescription';
import { ReactNode } from 'react';
import { IconType } from 'react-icons';

export interface Column<Item> {
  header: string;
  headerTooltip?: string;
  sortFieldName?: string;
  width?: number;
  renderItem?: (item: Item) => ReactNode;
  onClick?: (item: Item) => void;
  hideOnClick?: (item: Item) => boolean;
}

export interface Row<Item> {
  renderOverlay: (item: Item) => boolean;
  renderTooltip?: (item: Item) => string | undefined;
}

export interface GeneralDrawer<Item> {
  renderItem: (item: Item) => ReactNode;
}

export interface TableColumnAction<Item> {
  icon: ReactNode;
  variant?: 'filled' | 'outline';
  indicator?: (item: Item) => boolean;
  onClick?: (item: Item) => void;
  visible?: (item: Item) => boolean;
  tooltip: string;
  isLoading?: (item: Item) => boolean;
}

export interface TableAction {
  icon: IconType;
  label: string;
  onClick: (ids: string[]) => void;
  isUpdating: boolean;
  limit?: number;
  disabledTooltip?: string;
}

export interface TableActionConfig {
  tableActions: TableAction[];
  isUpdating: boolean;
  isUpdatingLabel: string;
}

export interface ReportsTableItem {
  id: string;
  trigger: string;
  content: { endTimestamp: Date; startTimestamp: Date };
  creationTimestamp: Date;
  tags: string[];
  status: string;
  updateTimestamp: Date;
  userId: string;
  groupId: string;
  firstName: string;
  lastName: string;
}

export interface PatientReportsTable {
  id: string;
  trigger: string;
  content: { endTimestamp: Date; startTimestamp: Date };
  creationTimestamp: Date;
  tags: string[];
  status: string;
  updateTimestamp: Date;
  userId: string;
  groupId: string;
}

export interface PatientProfile {
  id: string;
  firstName: string;
  lastName: string;
  gender?: number;
  birthday?: string;
  groups: PatientGroups[];
  lastMeasurement?: PatientMeasurement;
  mostSevereMeasurement?: PatientMeasurement;
  patientId?: string;
}

export const PORTAL_PATIENT_ID_PREFIX = 'portal-patient-id:';

export const getPortalPatientId = (externalPatientIds?: string[]): string | undefined =>
  externalPatientIds?.find((id) => id.startsWith(PORTAL_PATIENT_ID_PREFIX))?.slice(PORTAL_PATIENT_ID_PREFIX.length);

export interface UserProfile {
  id: string;
  firstName?: string;
  lastName?: string;
  gender: number;
  birthday: string;
  email?: string;
  phoneNumber?: string;
  language?: string;
  country: string;
  measurementSystem: string;
  hourFormat: string;
  physicianIdentifier?: string;
}

export interface PatientDetails {
  country: string;
  birthday: string;
  gender: number;
}

export interface PatientMeasurement {
  indicator: Indicator;
  timestamp: number;
  diagnosisWeight: number;
}

export interface PatientGroups {
  id: string;
  expired: boolean;
  daysLeft: number;
  activePeriod?: ActivePeriod;
  endDate: number;
  lastMeasurement?: PatientMeasurement;
  mostSevereMeasurement?: PatientMeasurement;
}

export enum Status {
  Active = 'Yes',
  Inactive = 'No',
  All = 'All',
}

export enum FilterChoiceOption {
  Yes = 'Yes',
  No = 'No',
  All = 'All',
}

export enum PatientStatus {
  Active = 'active',
  Inactive = 'inactive',
  All = 'all',
}

export enum PatientHasMeasurement {
  Yes = 'yes',
  No = 'no',
  All = 'all',
}

export enum Indicator {
  Urgent = 'urgent',
  Warning = 'warning',
  Normal = 'normal',
  Inconclusive = 'inconclusive',
  Quality = 'quality',
  NoResult = 'no_result',
  ResultUnavailable = 'result_unavailable',
}

export enum ReportOutcomeTag {
  Normal = 'indicator_normal',
  Warning = 'indicator_warning',
  Urgent = 'indicator_urgent',
  Quality = 'indicator_quality',
  Inconclusive = 'indicator_inconclusive',
  NoResult = 'indicator_no_result',
}

export interface ETLPatientProfileUser {
  id: string;
  firstName: string;
  lastName: string;
  cleanedFirstName: string;
  cleanedLastName: string;
  birthday: number;
  gender: number;
  externalPatientIds?: string[];
}

export interface ETLPatientProfileGroup {
  id: string;
  name: string;
}

export interface ETLPatientProfilePrescription {
  endTimestamp: number;
}

export interface ETLPatientProfileMeasurement {
  id: string;
  timestamp: number;
  mostSevereLabel: {
    color: string;
    key: string;
    weight: number;
  };
  weight: number;
}

export interface ETLPatientProfile {
  user: ETLPatientProfileUser;
  group: ETLPatientProfileGroup;
  prescription: ETLPatientProfilePrescription;
  lastMeasurement: ETLPatientProfileMeasurement;
  lastMostSevereMeasurement: ETLPatientProfileMeasurement;
  activePeriod: ActivePeriod;
}

export interface Staff {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

export enum ReportsStatus {
  Complete = 'COMPLETE',
  Incomplete = 'INCOMPLETE',
  New = 'NEW',
  All = 'all',
}
export interface Questions {
  arrhythmias: string;
  arteryDisease: string;
  atrialFibrillation: string;
  atrialFibrillationKnowledge: string;
  bloodThinner: string;
  diabetes: string;
  heartFailure: string;
  hypertension: string;
  pacemaker: string;
  stroke: string;
}

export interface SymptomRhythmTable {
  symptom: string;
  urgent: string;
  warning: string;
  normal: string;
  quality: string;
}
